import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ClipboardList } from 'lucide-react';
import { useState, useEffect } from 'react';
import { UserMenu } from './UserMenu';
import { useAuth } from '../contexts/AuthContext';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Helper for mobile navigation actions so we always close the overlay
  const handleMobileNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  // Helper for mobile sign out to keep behavior consistent across views
  const handleMobileSignOut = async () => {
    await logout();
    setIsOpen(false);
    navigate('/');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'glass py-2' : 'bg-transparent py-4'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2 group z-50 relative">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full opacity-0 group-hover:opacity-20 blur transition duration-500"></div>
              <img
                src="/bally-Market-sign-200px.png"
                alt="BallyMarket Logo"
                className="h-16 w-auto relative z-10 transform group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <UserMenu />
            <Link
              to="/register/trader"
              className="btn-primary flex items-center shadow-lg shadow-primary-900/20"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Register Your Business
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden z-50 relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-primary-900 hover:bg-white/20 focus:outline-none transition-all duration-300"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-surface-alt/95 backdrop-blur-xl transition-all duration-500 ease-in-out md:hidden flex flex-col justify-center items-center space-y-8 ${isOpen ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'
          }`}
      >
        {/* Decorative blobs */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-secondary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-200"></div>

        <div className="relative z-10 flex flex-col items-center space-y-6 w-full px-8">

          {/* Mobile auth actions: simplified user menu for small screens */}
          {user ? (
            <>
              <button
                onClick={() => handleMobileNavigate('/dashboard')}
                className="w-full max-w-xs btn-primary flex items-center justify-center py-4 text-lg shadow-xl shadow-primary-900/20"
              >
                Dashboard
              </button>
              <button
                onClick={handleMobileSignOut}
                className="w-full max-w-xs mt-3 flex items-center justify-center py-3 text-base rounded-full border border-red-300 text-red-600 bg-white/80 hover:bg-red-50 transition-colors"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => handleMobileNavigate('/login')}
                className="w-full max-w-xs flex items-center justify-center py-3 text-base rounded-full border border-primary-200 text-primary-900 bg-white/80 hover:bg-white transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => handleMobileNavigate('/register/user')}
                className="w-full max-w-xs btn-primary flex items-center justify-center py-4 text-lg shadow-xl shadow-primary-900/20 mt-3"
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}